import React, { useState } from 'react'
import {
  Button,
  Modal,
  PhoneInput,
  Checkbox
} from '@arnsbogroup/react-voicearchive'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useIntl, FormattedMessage } from 'react-intl'
import messages from './messages'

export default NiceModal.create(({ currentPhone, onSave }) => {
  const modal = useModal()
  const { formatMessage } = useIntl()
  const [phone, setPhone] = useState(currentPhone || '')
  const [country, setCountry] = useState('dk')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [termsError, setTermsError] = useState(false)

  const isValidPhone = (phoneNumber) => {
    // Basic validation for E.164 format: starts with + and has 8-15 digits
    return phoneNumber && phoneNumber.length >= 8 && phoneNumber.startsWith('+')
  }

  const onSubmit = () => {
    if (!isValidPhone(phone)) {
      setError(true)
      return
    }

    if (!agreedToTerms) {
      setTermsError(true)
      return
    }

    setLoading(true)
    setError(false)
    setTermsError(false)

    onSave(phone)
      .then(() => {
        toastr.success(formatMessage(messages.saveSuccess))
        modal.resolve(phone)
        modal.remove()
      })
      .catch((error) => {
        toastr.error(formatMessage(messages.saveError))
        setLoading(false)
      })
  }

  const onCancel = () => {
    modal.reject()
    modal.remove()
  }

  return (
    <Modal
      show={modal.visible}
      onClose={onCancel}
      title={formatMessage(messages.modalTitle)}
      dialogPanelClasses="max-w-lg"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          <FormattedMessage {...messages.modalDescription} />
        </p>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            <FormattedMessage {...messages.phoneNumberLabel} />
          </label>
          <PhoneInput
            country={country}
            value={phone}
            onChange={(phoneNumber, countryData) => {
              const formattedPhone = phoneNumber ? '+' + phoneNumber : ''
              setPhone(formattedPhone)
              setCountry(countryData.countryCode)
              setError(false)
            }}
            placeholder={formatMessage(messages.phoneNumberPlaceholder)}
            enableSearch
            disableSearchIcon
            containerClass={`w-full border ${error ? 'border-red-600' : 'border-gray-400'} focus:border-primary transition duration-200`}
            inputClass="w-full py-4 h-auto"
            inputStyle={{ width: '100%', height: 'auto', border: 'initial' }}
            buttonStyle={{ border: 'initial' }}
            searchPlaceholder={formatMessage(messages.phoneNumberSearchPlaceholder)}
            searchNotFound={formatMessage(messages.phoneNumberNotFoundPlaceholder)}
          />
          {error && (
            <p className="text-sm text-red-600">
              <FormattedMessage {...messages.invalidPhoneError} />
            </p>
          )}
          <p className="text-xs text-gray-500">
            <FormattedMessage {...messages.phoneNumberHint} />
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex items-start">
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => {
                setAgreedToTerms(e.target.checked)
                setTermsError(false)
              }}
            />
            <label className="ml-2 text-sm text-gray-700">
              <FormattedMessage
                {...messages.privacyPolicyLabel}
                values={{
                  privacyPolicy: (
                    <a
                      href="https://www.voicearchive.com/privacy-policy"
                      target="_blank"
                      className="text-primary hover:underline"
                    >
                      <FormattedMessage {...messages.privacyPolicy} />
                    </a>
                  ),
                  terms: (
                    <a
                      href="https://www.voicearchive.com/terms-conditions"
                      target="_blank"
                      className="text-primary hover:underline"
                    >
                      <FormattedMessage {...messages.termsAndConditions} />
                    </a>
                  ),
                }}
              />
            </label>
          </div>
          {termsError && (
            <p className="text-sm text-red-600">
              <FormattedMessage {...messages.privacyPolicyRequired} />
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button
            onClick={onSubmit}
            disabled={loading}
          >
            {loading ? (
              <FormattedMessage {...messages.saving} />
            ) : (
              <FormattedMessage {...messages.save} />
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
})
